import { Paperclip, CreditCard } from 'lucide-react';
import { formatDate, formatCurrency, formatHtTtc } from '../lib/format.js';
import { INTERNAL_QONTO_TYPES } from '../lib/transaction-utils.js';

/**
 * Ligne de transaction — responsive :
 * - Mobile (<md) : logo + (nom/date/cat/note) empilés + montant à droite
 * - Desktop (>=md) : colonnes fixes — logo | date | catégorie | nom | montant
 */

/**
 * Retourne true si la transaction est un débit avec une catégorie métier assignée —
 * condition nécessaire pour afficher le badge « TVA ? ».
 */
function shouldShowMissingVatBadge(tx) {
  if (tx.amount_cents >= 0) return false;
  if (!tx.category_id && !tx.category_name) return false;
  if (INTERNAL_QONTO_TYPES.has(tx.operation_type)) return false;
  return true;
}

function vatSourceLabel(source) {
  if (source === 'qonto_auto') return 'auto (Qonto)';
  if (source === 'inferred') return 'auto (déduit)';
  if (source === 'manual') return 'manuelle';
  return source ?? '';
}

/**
 * @param {boolean} [showHtFormat=false] — quand true et que tx.vat_amount_cents est non nul,
 *   affiche "X € HT (Y € TTC)" au lieu du montant brut seul. Utilisé dans le drill-down
 *   cash-flow pour les transactions avec TVA connue.
 */
export default function TransactionRow({ transaction, categories = [], onClick, compact = false, showHtFormat = false }) {
  const tx = transaction;
  const isDebit = tx.side === 'debit' || tx.amount_cents < 0;
  const amount = Math.abs(tx.amount_cents ?? 0);

  const categoryName =
    tx.category_name ??
    categories.find((c) => c.id === tx.category_id)?.name ??
    null;

  const displayName =
    tx.clean_counterparty_name ??
    tx.counterparty_name ??
    tx.label ??
    '—';

  const subInfo = tx.qonto_cashflow_subcategory ?? tx.qonto_cashflow_category ?? tx.qonto_category ?? null;
  const dateText = formatDate(tx.settled_at ?? tx.operation_date);

  return (
    <button
      type="button"
      onClick={() => onClick?.(tx)}
      className="w-full text-left hover:bg-slate-50 transition-colors cursor-pointer focus-visible:outline-none focus-visible:bg-slate-50"
      aria-label={`Voir le détail de ${displayName}`}
    >
      {/* Layout mobile (<md) — empilé. Forcé sur tous breakpoints si compact=true. */}
      <div className={`${compact ? 'flex' : 'flex md:hidden'} items-center gap-3 px-4 py-3`}>
        {tx.logo_url ? (
          <img
            src={tx.logo_url}
            alt=""
            className="w-9 h-9 rounded-full shrink-0 bg-slate-100 object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-9 h-9 rounded-full shrink-0 bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500">
            {displayName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-slate-800 truncate">{displayName}</p>
            {tx.attachments_count > 0 && (
              <Paperclip size={12} className="shrink-0 text-slate-400" aria-label="Pièce jointe" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-slate-400">{dateText}</span>
            {categoryName && (
              <span className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded truncate max-w-[200px]">
                {categoryName}
              </span>
            )}
            {!categoryName && subInfo && (
              <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded truncate max-w-[200px]" title="Catégorie Qonto native">
                {subInfo}
              </span>
            )}
            {tx.status === 'pending' && (
              <span className="text-xs bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded">en attente</span>
            )}
            {tx.bank_provider === 'wise' && (
              <span className="text-[10px] bg-sky-50 text-sky-700 ring-1 ring-sky-200 px-1.5 py-0.5 rounded font-medium">Wise</span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          {showHtFormat && tx.vat_amount_cents ? (
            /* Mode HT/TTC : "X € HT (Y € TTC)" */
            <span className={`text-sm font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
              {isDebit ? '-' : ''}{formatHtTtc(Math.abs(tx.amount_cents ?? 0), Math.abs(tx.vat_amount_cents))}
            </span>
          ) : (
            <>
              <span className={`text-sm font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
                {isDebit ? '-' : '+'}{formatCurrency(amount)}
              </span>
              {tx.vat_amount_cents > 0 ? (
                <p
                  className="text-[10px] text-slate-400 tabular-nums"
                  title={`TVA ${tx.vat_rate}% — source ${vatSourceLabel(tx.vat_source)}`}
                >
                  dont {formatCurrency(tx.vat_amount_cents)} TVA
                </p>
              ) : shouldShowMissingVatBadge(tx) && (
                <p
                  className="text-[10px] text-orange-500 hover:text-orange-700 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); onClick?.(tx); }}
                >
                  TVA ?
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Layout desktop (>=md) — colonnes : logo | date | fournisseur | catégorie | montant.
          Masqué quand compact=true (on reste sur l'empilé quelque soit le breakpoint). */}
      <div className={`${compact ? 'hidden' : 'hidden md:grid'} md:grid-cols-[40px_100px_minmax(180px,1.5fr)_minmax(160px,1fr)_140px] md:gap-4 md:items-center md:px-4 md:py-2.5`}>
        {/* Colonne 1 : logo */}
        <div className="shrink-0">
          {tx.logo_url ? (
            <img
              src={tx.logo_url}
              alt=""
              className="w-8 h-8 rounded-full bg-slate-100 object-cover"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500">
              {displayName.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>

        {/* Colonne 2 : date */}
        <div className="text-xs text-slate-500 tabular-nums">
          {dateText}
          {tx.status === 'pending' && (
            <span className="block text-[10px] text-yellow-700">en attente</span>
          )}
        </div>

        {/* Colonne 3 : fournisseur (nom + méta note, attachment, card) */}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-slate-800 truncate">{displayName}</p>
            {tx.attachments_count > 0 && (
              <Paperclip size={12} className="shrink-0 text-slate-400" aria-label="Pièce jointe" />
            )}
            {tx.operation_type === 'card' && tx.card_last_digits && (
              <span className="text-[10px] text-slate-400 shrink-0 inline-flex items-center gap-0.5">
                <CreditCard size={10} />•{tx.card_last_digits}
              </span>
            )}
          </div>
          {tx.note && (
            <p className="text-xs text-slate-400 italic truncate">« {tx.note} »</p>
          )}
        </div>

        {/* Colonne 4 : catégorie */}
        <div className="min-w-0">
          {categoryName ? (
            <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded truncate inline-block max-w-full">
              {categoryName}
            </span>
          ) : subInfo ? (
            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded truncate inline-block max-w-full" title="Catégorie Qonto">
              {subInfo}
            </span>
          ) : (
            <span className="text-xs text-slate-300 italic">Non classifiée</span>
          )}
        </div>

        {/* Colonne 5 : montant + montant TVA sous le total */}
        <div className="text-right">
          {showHtFormat && tx.vat_amount_cents ? (
            <span className={`text-sm font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
              {isDebit ? '-' : ''}{formatHtTtc(Math.abs(tx.amount_cents ?? 0), Math.abs(tx.vat_amount_cents))}
            </span>
          ) : (
            <>
              <span className={`text-sm font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
                {isDebit ? '-' : '+'}{formatCurrency(amount)}
              </span>
              {tx.vat_amount_cents > 0 ? (
                <p
                  className="text-[10px] text-slate-400 tabular-nums"
                  title={`TVA ${tx.vat_rate}% — source ${vatSourceLabel(tx.vat_source)}`}
                >
                  dont {formatCurrency(tx.vat_amount_cents)} TVA
                </p>
              ) : shouldShowMissingVatBadge(tx) && (
                <p
                  className="text-[10px] text-orange-500 hover:text-orange-700 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); onClick?.(tx); }}
                >
                  TVA ?
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </button>
  );
}
